/**
 * Learn more about theme events at
 * https://help.switchthemes.co/shapes/developer/theme-events/overview.html
 */

window.theme = window.theme || {};

window.theme.events = {
  version: '1.0',
};

/**
 * Shopper turned “gift” on in this tab. Footer morphs + GET /cart.js often lag POST /cart/update.js;
 * without this they replace the checkbox with stale HTML and it unchecks before you can close the drawer.
 * Checkout still uses `cart.note`; this only stabilizes UI until the server matches.
 */
const CART_DRAWER_GIFT_CHECKED_INTENT_KEY = 'theme:cartDrawerGiftChecked';
const CART_DRAWER_GIFT_NOTE_PANEL_OPEN_KEY = 'theme:cartDrawerGiftNotePanelOpen';

function persistGiftNotePanelOpen(open) {
  try {
    if (open) sessionStorage.setItem(CART_DRAWER_GIFT_NOTE_PANEL_OPEN_KEY, '1');
    else sessionStorage.removeItem(CART_DRAWER_GIFT_NOTE_PANEL_OPEN_KEY);
  } catch (e) {
    /* private / disabled storage */
  }
}

function isGiftNotePanelOpenPersisted() {
  try {
    return sessionStorage.getItem(CART_DRAWER_GIFT_NOTE_PANEL_OPEN_KEY) === '1';
  } catch (e) {
    return false;
  }
}

window.theme.persistGiftNotePanelOpen = persistGiftNotePanelOpen;
window.theme.isGiftNotePanelOpenPersisted = isGiftNotePanelOpenPersisted;

function persistGiftCheckboxIntent(checked) {
  try {
    if (checked) sessionStorage.setItem(CART_DRAWER_GIFT_CHECKED_INTENT_KEY, '1');
    else {
      sessionStorage.removeItem(CART_DRAWER_GIFT_CHECKED_INTENT_KEY);
      persistGiftNotePanelOpen(false);
    }
  } catch (e) {
    /* private / disabled storage */
  }
}

function isGiftCheckboxIntentPersisted() {
  try {
    return sessionStorage.getItem(CART_DRAWER_GIFT_CHECKED_INTENT_KEY) === '1';
  } catch (e) {
    return false;
  }
}

window.theme.persistGiftCheckboxIntent = persistGiftCheckboxIntent;
window.theme.isGiftCheckboxIntentPersisted = isGiftCheckboxIntentPersisted;

const CART_DRAWER_GIFT_PRODUCT_INTENT_KEY = 'theme:cartDrawerGiftProduct';

function persistGiftProductIntent(checked) {
  try {
    if (checked) sessionStorage.setItem(CART_DRAWER_GIFT_PRODUCT_INTENT_KEY, '1');
    else sessionStorage.removeItem(CART_DRAWER_GIFT_PRODUCT_INTENT_KEY);
  } catch (e) {
    /* private / disabled storage */
  }
}

function isGiftProductIntentPersisted() {
  try {
    return sessionStorage.getItem(CART_DRAWER_GIFT_PRODUCT_INTENT_KEY) === '1';
  } catch (e) {
    return false;
  }
}

window.theme.persistGiftProductIntent = persistGiftProductIntent;
window.theme.isGiftProductIntentPersisted = isGiftProductIntentPersisted;

function cartHasManagedGiftUpsell(cart) {
  const bagSettings = window.theme.settings && window.theme.settings.giftBag;
  if (!bagSettings || !cart || !cart.items) return false;
  const variantId = String(
    bagSettings.variantId || bagSettings.manualVariantId || ''
  ).trim();
  return cart.items.some((item) => {
    const props = item.properties || {};
    if (!props._auto_gift_bag) return false;
    if (!variantId) return true;
    return String(item.variant_id) === variantId;
  });
}

window.theme.cartHasManagedGiftUpsell = cartHasManagedGiftUpsell;

function dispatchGiftUpsellProductState(added) {
  document.body.dispatchEvent(
    new CustomEvent('cart-drawer-gift-product-state', {
      bubbles: true,
      composed: true,
      detail: { added: !!added },
    })
  );
}

function noteLooksLikeGiftDrawerNote(note, line) {
  const n = String(note || '').trim();
  const l = String(line || '').trim();
  if (!l) return false;
  const lfBreak = '\n\n';
  const crlfBreak = '\r\n\r\n';
  return n === l || n.startsWith(l + lfBreak) || n.startsWith(l + crlfBreak);
}

window.theme.noteLooksLikeGiftDrawerNote = noteLooksLikeGiftDrawerNote;

/** If Liquid already rendered a gift note in the hidden field, treat gift as on (helps first paint). */
function seedGiftIntentFromHiddenNoteField() {
  const compose = document.querySelector('[data-cart-drawer-gift-compose]');
  const hidden = document.getElementById('CartDrawerCartNoteField');
  if (!compose || !hidden) return;
  const line = (compose.dataset.giftOrderLine || '').trim();
  if (!line) return;
  if (noteLooksLikeGiftDrawerNote(hidden.value, line)) persistGiftCheckboxIntent(true);
}

function applyGiftCheckboxIntentFromStorage() {
  if (!isGiftCheckboxIntentPersisted()) return;
  const drawer = document.querySelector('[data-cart-drawer]');
  const compose = document.querySelector('[data-cart-drawer-gift-compose]');
  if (!drawer || !compose) return;
  const line = (compose.dataset.giftOrderLine || '').trim();
  if (!line) return;
  const cb = drawer.querySelector('[data-cart-drawer-gift-checkbox]');
  if (cb) cb.checked = true;
  const ta = document.getElementById('CartDrawerGiftNote');
  const custom = ta ? String(ta.value || '').trim() : '';
  const hiddenNote = document.getElementById('CartDrawerCartNoteField');
  if (hiddenNote) {
    const cur = String(hiddenNote.value || '').trim();
    if (!noteLooksLikeGiftDrawerNote(cur, line)) {
      hiddenNote.value = line + (custom ? '\n\n' + custom : '');
    }
  }
  document.body.dispatchEvent(
    new CustomEvent('cart-drawer-gift-sync-checked', {
      bubbles: true,
      composed: true,
      detail: { checked: true },
    })
  );
}

window.theme.applyGiftCheckboxIntentFromStorage = applyGiftCheckboxIntentFromStorage;

/** Gift UI from cart JSON (prefer POST response). `intent` prevents stale morph/GET from unchecking. */
function updateDrawerGiftUIFromCart(cart) {
  if (!cart) return;
  const drawer = document.querySelector('[data-cart-drawer]');
  const compose = document.querySelector('[data-cart-drawer-gift-compose]');
  if (!drawer || !compose) return;

  const line = (compose.dataset.giftOrderLine || '').trim();
  if (!line) return;

  if (!cart.items || cart.items.length === 0) {
    persistGiftCheckboxIntent(false);
    persistGiftProductIntent(false);
    const cbEmpty = drawer.querySelector('[data-cart-drawer-gift-checkbox]');
    if (cbEmpty) cbEmpty.checked = false;
    const hiddenEmpty = document.getElementById('CartDrawerCartNoteField');
    if (hiddenEmpty && cart.note != null) hiddenEmpty.value = String(cart.note);
    document.body.dispatchEvent(
      new CustomEvent('cart-drawer-gift-sync-checked', {
        bubbles: true,
        composed: true,
        detail: { checked: false },
      })
    );
    document.body.dispatchEvent(
      new CustomEvent('cart-drawer-gift-note-state', {
        bubbles: true,
        composed: true,
        detail: { hasNote: false },
      })
    );
    dispatchGiftUpsellProductState(false);
    return;
  }

  const productInCart = cartHasManagedGiftUpsell(cart);
  if (productInCart) persistGiftProductIntent(true);
  dispatchGiftUpsellProductState(productInCart);

  const note = String(cart.note ?? '').trim();
  const crlfBreak = '\r\n\r\n';
  const lfBreak = '\n\n';
  const serverSaysGift =
    note === line ||
    note.startsWith(line + lfBreak) ||
    note.startsWith(line + crlfBreak);
  const intentOn = isGiftCheckboxIntentPersisted();
  const isGift = intentOn || serverSaysGift;
  if (serverSaysGift) persistGiftCheckboxIntent(true);

  let hasCustom = false;
  if (intentOn && !serverSaysGift) {
    const ta = document.getElementById('CartDrawerGiftNote');
    hasCustom = !!(ta && String(ta.value || '').trim());
  } else if (note === line) {
    hasCustom = false;
  } else if (note.startsWith(line + lfBreak)) {
    hasCustom = note.slice(line.length + lfBreak.length).trim().length > 0;
  } else if (note.startsWith(line + crlfBreak)) {
    hasCustom =
      note.slice(line.length + crlfBreak.length).trim().length > 0;
  } else if (note.length > 0 && !serverSaysGift) {
    hasCustom = true;
  }

  const cb = drawer.querySelector('[data-cart-drawer-gift-checkbox]');
  if (cb) {
    cb.checked = isGift;
  }

  const hiddenNote = document.getElementById('CartDrawerCartNoteField');
  if (hiddenNote) {
    if (cart.note != null && String(cart.note).trim() !== '') {
      hiddenNote.value = String(cart.note);
    } else if (intentOn && line) {
      const ta = document.getElementById('CartDrawerGiftNote');
      const custom = ta ? String(ta.value || '').trim() : '';
      hiddenNote.value = line + (custom ? '\n\n' + custom : '');
    }
  }

  document.body.dispatchEvent(
    new CustomEvent('cart-drawer-gift-sync-checked', {
      bubbles: true,
      composed: true,
      detail: { checked: isGift },
    })
  );

  document.body.dispatchEvent(
    new CustomEvent('cart-drawer-gift-note-state', {
      bubbles: true,
      composed: true,
      detail: { hasNote: hasCustom },
    })
  );
}

window.theme.updateDrawerGiftUIFromCart = updateDrawerGiftUIFromCart;

/**
 * Align “This is a gift” checkbox with cart note via GET /cart.js.
 */
function syncDrawerGiftCheckboxFromCart() {
  const drawer = document.querySelector('[data-cart-drawer]');
  const compose = document.querySelector('[data-cart-drawer-gift-compose]');
  if (!drawer || !compose) return;

  const line = (compose.dataset.giftOrderLine || '').trim();
  if (!line) return;

  fetch(`${String(window.theme.routes.cart_url || '/cart').replace(/\/$/, '')}.js`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
    .then((r) => r.json())
    .then((cart) => updateDrawerGiftUIFromCart(cart))
    .catch(() => {});
}

window.theme.syncDrawerGiftCheckboxFromCart = syncDrawerGiftCheckboxFromCart;

/** Compose drawer gift line + optional custom text (must match cart-footer Liquid). */
function getComposedDrawerCartNote() {
  const compose = document.querySelector('[data-cart-drawer-gift-compose]');
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!compose || !drawer) return '';
  const line = (compose.dataset.giftOrderLine || '').trim();
  const cb = drawer.querySelector('[data-cart-drawer-gift-checkbox]');
  const ta = document.getElementById('CartDrawerGiftNote');
  const custom = ta ? String(ta.value || '').trim() : '';
  const intentOn = isGiftCheckboxIntentPersisted();
  if ((cb && cb.checked) || intentOn) {
    return line + (custom ? '\n\n' + custom : '');
  }
  return custom;
}

window.theme.getComposedDrawerCartNote = getComposedDrawerCartNote;

/** sessionStorage backup: survives full page navigations when /cart/update.js is dropped or races. */
const CART_DRAWER_GIFT_NOTE_BACKUP_KEY = 'theme:cartDrawerGiftNoteBackup';

function setCartDrawerGiftNoteBackup(note) {
  try {
    if (note != null && String(note).trim() !== '') {
      sessionStorage.setItem(CART_DRAWER_GIFT_NOTE_BACKUP_KEY, String(note));
    } else {
      sessionStorage.removeItem(CART_DRAWER_GIFT_NOTE_BACKUP_KEY);
    }
  } catch (e) {
    /* private / disabled storage */
  }
}

window.theme.setCartDrawerGiftNoteBackup = setCartDrawerGiftNoteBackup;

async function reconcileCartDrawerGiftNoteFromBackup() {
  if (!window.theme?.routes?.cart_update_url) return;
  let backup;
  try {
    backup = sessionStorage.getItem(CART_DRAWER_GIFT_NOTE_BACKUP_KEY);
  } catch (e) {
    return;
  }
  if (backup == null || backup === '') return;

  const cartJs = `${String(window.theme.routes.cart_url || '/cart').replace(/\/$/, '')}.js`;
  let cart;
  try {
    const r = await fetch(cartJs, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!r.ok) return;
    cart = await r.json();
  } catch (e) {
    return;
  }

  if (!cart.items || cart.items.length === 0) {
    setCartDrawerGiftNoteBackup('');
    persistGiftCheckboxIntent(false);
    return;
  }

  if (String(cart.note ?? '') === backup) return;

  try {
    const up = await fetch(window.theme.routes.cart_update_url, {
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify({ note: backup }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    if (up.ok) {
      try {
        const data = await up.json();
        updateDrawerGiftUIFromCart(data);
      } catch (e) {
        syncDrawerGiftCheckboxFromCart();
      }
    }
  } catch (e) {
    /* ignore */
  }
}

function scheduleCartDrawerGiftNoteReconcile() {
  queueMicrotask(() => {
    seedGiftIntentFromHiddenNoteField();
    applyGiftCheckboxIntentFromStorage();
    reconcileCartDrawerGiftNoteFromBackup();
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCartDrawerGiftNoteReconcile);
  } else {
    scheduleCartDrawerGiftNoteReconcile();
  }
  window.addEventListener('pageshow', (ev) => {
    if (ev.persisted) scheduleCartDrawerGiftNoteReconcile();
  });
}

/** Checkout POST must include the real note; hidden field stays in sync. */
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (!form || form.id !== 'cart') return;
    const sub = e.submitter;
    if (!sub || sub.getAttribute('name') !== 'checkout') return;
    const hidden = document.getElementById('CartDrawerCartNoteField');
    if (!hidden) return;
    hidden.value = getComposedDrawerCartNote();
  },
  true
);

document.addEventListener('theme:update:cart', async () => {
  document.dispatchEvent(new CustomEvent('shapes:cart:lock'));

  try {
    const pathWithQuery =
      window.location.pathname + (window.location.search || '');
    const querySep = pathWithQuery.includes('?') ? '&' : '?';
    const sectionUrl = `${pathWithQuery}${querySep}sections=cart-items,cart-footer,cart-item-count,cart-live-region,cart-drawer-free-shipping&_section_t=${Date.now()}`;
    const response = await fetch(sectionUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error('Cart sections request failed');
    }

    if (!data.status) {
      /* Section HTML re-renders cart-footer from `cart` (order note + gift checkbox), like /cart. */
      document.dispatchEvent(
        new CustomEvent('shapes:cart:update', {
          bubbles: true,
          detail: {
            response: {
              sections: {
                ...data,
              },
            },
          },
        })
      );
    }
  } catch (e) {
    console.error(e);
    document.getElementById('cart-errors').textContent =
      theme.strings.cartError;
    document.dispatchEvent(
      new CustomEvent('theme:cart:error:other', {
        detail: {
          message: theme.strings.cartError,
          error: e,
        },
      })
    );
  } finally {
    document.dispatchEvent(new CustomEvent('shapes:cart:unlock'));
  }
});

function morphCartDrawerFreeShipping(e) {
  const sections = e.detail?.response?.sections;
  if (!sections?.['cart-drawer-free-shipping']) return;
  const root = document.querySelector('[data-cart-drawer-free-shipping-root]');
  if (!root) return;
  const html = sections['cart-drawer-free-shipping'];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-cart-drawer-free-shipping-root]');
  if (fresh) {
    root.innerHTML = fresh.innerHTML;
  }
}

document.addEventListener('shapes:cart:update', morphCartDrawerFreeShipping);
document.addEventListener('shapes:cart:cartqtychange', morphCartDrawerFreeShipping);
document.addEventListener('shapes:cart:afteradditem', morphCartDrawerFreeShipping);

async function fetchCartComplementaryHTML(url) {
  const response = await fetch(url, { credentials: 'same-origin' });
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fragment = doc.querySelector('.related-products');
  return fragment ? fragment.innerHTML : '';
}

async function hydrateCartComplementaryIslands(root) {
  if (!root) return;

  const slider = root.querySelector('.cart-comp-slider');
  if (slider && window.Alpine) {
    window.Alpine.initTree(slider);
  }
}

async function handleCartDrawerComplementarySubmit(event) {
  const form = event.target.closest(
    '.cart-drawer-complementary-edge form.cart-drawer-comp-form'
  );
  if (!form) return;

  event.preventDefault();
  event.stopPropagation();

  if (form.dataset.complementaryAdding === 'true') return;
  form.dataset.complementaryAdding = 'true';

  const errorEl = form.querySelector('[data-complementary-error]');
  if (errorEl) errorEl.textContent = '';

  try {
    const formData = new FormData(form);
    const modalCart = window.theme?.settings?.cartType === 'modal';

    if (modalCart) {
      formData.append(
        'sections',
        'cart-items,cart-footer,cart-item-count,cart-live-region,cart-drawer-free-shipping'
      );
      formData.append('sections_url', window.location.pathname);
    }

    const response = await fetch(window.theme.routes.cart_add_url, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
    });

    const data = await response.json();

    if (data.status) {
      if (errorEl) {
        errorEl.textContent =
          data.description ||
          data.message ||
          window.theme.strings.cartError;
      }
      return;
    }

    document.body.dispatchEvent(
      new CustomEvent('shapes:cart:afteradditem', {
        bubbles: true,
        detail: { response: data },
      })
    );
  } catch (error) {
    console.error(error);
    if (errorEl) {
      errorEl.textContent = window.theme.strings.cartError;
    }
  } finally {
    delete form.dataset.complementaryAdding;
  }
}

document.addEventListener('submit', handleCartDrawerComplementarySubmit, true);

function useCartDrawerAjax() {
  return (
    window.theme?.settings?.cartType === 'modal' &&
    document.querySelector('[data-cart-drawer]')
  );
}

function openCartDrawerFromTheme() {
  if (window.theme?.settings?.cartType !== 'modal') return;

  const tryOpen = () => {
    const modals = window.Alpine?.store?.('modals');
    if (modals?.modals?.cart) {
      modals.closeAll?.();
      modals.open('cart');
      return true;
    }
    return false;
  };

  if (tryOpen()) return;

  if (!window.Alpine) return;

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (tryOpen() || attempts > 40) {
      window.clearInterval(timer);
    }
  }, 50);
}

async function handleQuickBuyFormSubmit(event) {
  const form = event.target.closest('form.quick-buy-product-form');
  if (!form) return;

  if (form.classList.contains('cart-drawer-comp-form')) return;

  const island = form.closest('data-island');
  if (island?.hasAttribute('ready')) return;

  event.preventDefault();
  event.stopPropagation();

  if (form.dataset.quickBuySubmitting === 'true') return;
  form.dataset.quickBuySubmitting = 'true';

  const submitButton = form.querySelector('[type="submit"]');
  if (submitButton) submitButton.disabled = true;

  try {
    const formData = new FormData(form);

    if (useCartDrawerAjax()) {
      formData.append(
        'sections',
        'cart-items,cart-footer,cart-item-count,cart-live-region,cart-drawer-free-shipping'
      );
      formData.append('sections_url', window.location.pathname);
    }

    const response = await fetch(window.theme.routes.cart_add_url, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
    });

    const data = await response.json();

    if (data.status) {
      const message = data.description || data.message || window.theme.strings.cartError;
      document.body.dispatchEvent(
        new CustomEvent('shapes:cart:adderror', {
          bubbles: true,
          detail: {
            source: 'product-form',
            sourceId: form.getAttribute('id'),
            variantId: Number(formData.get('id')),
            errors: data.errors || data.description,
            message,
          },
        })
      );
      return;
    }

    if (useCartDrawerAjax()) {
      document.body.dispatchEvent(
        new CustomEvent('shapes:cart:afteradditem', {
          bubbles: true,
          detail: { response: data },
        })
      );
    } else {
      window.location.assign(window.theme.routes.cart_url);
    }
  } catch (error) {
    console.error(error);
  } finally {
    delete form.dataset.quickBuySubmitting;
    if (submitButton) submitButton.disabled = false;
  }
}

document.addEventListener('submit', handleQuickBuyFormSubmit, true);

async function fetchCartSectionsForDrawer() {
  const pathWithQuery =
    window.location.pathname + (window.location.search || '');
  const querySep = pathWithQuery.includes('?') ? '&' : '?';
  const sectionUrl = `${pathWithQuery}${querySep}sections=cart-items,cart-footer,cart-item-count,cart-live-region,cart-drawer-free-shipping&_section_t=${Date.now()}`;
  const response = await fetch(sectionUrl, {
    credentials: 'same-origin',
    cache: 'no-store',
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error('Cart sections request failed');
  }
  return data;
}

function readProductFormQuantity(form) {
  const qtyInput = form.querySelector('[name="quantity"]');
  if (!qtyInput) return 1;
  const parsed = parseInt(String(qtyInput.value || '1'), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

async function handlePdpMetafieldUpsellAddBoth(event) {
  const btn = event.target.closest('[data-pdp-upsell-add-both]');
  if (!btn || btn.dataset.adding === '1') return;
  if (btn.hasAttribute('disabled')) return;

  const upsellRoot = btn.closest('[data-pdp-metafield-upsell]');
  if (!upsellRoot) return;

  const formId = String(upsellRoot.dataset.productFormId || '').trim();
  const form =
    (formId && document.getElementById(formId)) ||
    upsellRoot.closest('[data-product-root]')?.querySelector('form.shopify-product-form') ||
    upsellRoot.closest('data-island')?.querySelector('form.shopify-product-form') ||
    btn.closest('form.shopify-product-form');
  if (!form) return;

  event.preventDefault();
  event.stopPropagation();

  const upsellVariantId = String(
    upsellRoot.dataset.upsellVariantId || ''
  ).trim();
  const mainVariantInput = form.querySelector('[name="id"]');
  const mainVariantId = mainVariantInput
    ? String(mainVariantInput.value || '').trim()
    : '';

  if (!upsellVariantId || !mainVariantId) return;

  const errorEl = upsellRoot.querySelector('[data-pdp-upsell-error]');
  if (errorEl) errorEl.textContent = '';

  const quantity = readProductFormQuantity(form);
  const cartAddUrl =
    window.theme?.routes?.cart_add_url || '/cart/add.js';
  const modalCart = useCartDrawerAjax();
  const productFormId = form.getAttribute('id') || formId;

  btn.dataset.adding = '1';
  btn.classList.add('is-loading');

  try {
    const addRes = await fetch(cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          { id: mainVariantId, quantity },
          { id: upsellVariantId, quantity: 1 },
        ],
      }),
    });

    const addPayload = await addRes.json();

    if (!addRes.ok || addPayload.status) {
      const message =
        addPayload.description ||
        addPayload.message ||
        window.theme?.strings?.cartError ||
        'Could not add items to cart.';
      if (errorEl) errorEl.textContent = message;
      document.body.dispatchEvent(
        new CustomEvent('shapes:cart:adderror', {
          bubbles: true,
          detail: {
            source: 'pdp-metafield-upsell',
            sourceId: productFormId,
            variantId: Number(mainVariantId),
            errors: addPayload.errors || addPayload.description,
            message,
          },
        })
      );
      return;
    }

    let response = addPayload;
    if (modalCart) {
      try {
        const sections = await fetchCartSectionsForDrawer();
        response = { ...addPayload, sections };
      } catch (sectionError) {
        console.error(sectionError);
        document.dispatchEvent(new CustomEvent('theme:update:cart'));
      }

      document.body.dispatchEvent(
        new CustomEvent('shapes:cart:afteradditem', {
          bubbles: true,
          detail: { response, sourceId: productFormId },
        })
      );
    } else {
      window.location.assign(window.theme.routes.cart_url);
    }
  } catch (error) {
    console.error(error);
    if (errorEl) {
      errorEl.textContent =
        window.theme?.strings?.cartError || 'Could not add items to cart.';
    }
  } finally {
    delete btn.dataset.adding;
    btn.classList.remove('is-loading');
  }
}

document.addEventListener('click', handlePdpMetafieldUpsellAddBoth, true);

function pdpSendHintIsValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function pdpSendHintBuildEventProperties({ senderName, product }) {
  const props = {
    sender_name: senderName,
    SenderName: senderName,
    product_title: product.title,
    ProductName: product.title,
    product_url: product.url,
    ProductURL: product.url,
    URL: product.url,
    product_image_url: product.imageUrl,
    ImageURL: product.imageUrl,
    product_handle: product.handle,
    product_id: product.id,
    variant_id: product.variantId,
  };

  return props;
}

const PDP_SEND_HINT_KLAVIYO_COMPANY_ID = 'WCS8Pe';

function pdpSendHintKlaviyoCompanyId() {
  const script = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
  const match = script?.src.match(/company_id=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : PDP_SEND_HINT_KLAVIYO_COMPANY_ID;
}

function pdpSendHintProfileProperties({ senderName, product }) {
  return {
    last_hint_sender_name: senderName,
    last_hint_product_title: product.title,
    last_hint_product_url: product.url,
    last_hint_product_image_url: product.imageUrl,
    last_hint_variant_id: product.variantId,
  };
}

function pdpSendHintBase64(value) {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

// Klaviyo's /client/ API needs a CORS preflight, which some browsers and networks fail.
// These endpoints accept simple requests, so they go through regardless of consent or CORS.
async function pdpSendHintSubscribeToList({ listId, recipientEmail, senderName, product }) {
  const profileProperties = pdpSendHintProfileProperties({ senderName, product });
  const body = new URLSearchParams({
    g: listId,
    email: recipientEmail,
    $source: 'Drop a Hint',
    $consent: 'email',
    $fields: ['$source', '$consent', ...Object.keys(profileProperties)].join(','),
  });
  Object.entries(profileProperties).forEach(([key, value]) => {
    if (value !== undefined && value !== null) body.append(key, String(value));
  });

  const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
    method: 'POST',
    body,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success === false) {
    throw new Error(`Klaviyo subscribe failed: ${JSON.stringify(result.errors || response.status)}`);
  }
}

async function pdpSendHintTrackKlaviyoEvent({
  metricName,
  recipientEmail,
  senderName,
  product,
}) {
  const properties = pdpSendHintBuildEventProperties({ senderName, product });
  properties.$event_id = `hint-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  if (product.price) properties.$value = Number(product.price) / 100;

  const data = pdpSendHintBase64({
    token: pdpSendHintKlaviyoCompanyId(),
    event: metricName,
    customer_properties: {
      $email: recipientEmail,
      ...pdpSendHintProfileProperties({ senderName, product }),
    },
    properties,
  });
  const url = `https://fast.a.klaviyo.com/api/track?data=${encodeURIComponent(data)}`;

  try {
    await fetch(url, { mode: 'no-cors', keepalive: true });
  } catch (error) {
    await new Promise((resolve) => {
      const beacon = new Image();
      beacon.onload = resolve;
      beacon.onerror = resolve;
      beacon.src = url;
    });
  }
}

function pdpSendHintModalRoot(formOrModal) {
  return (
    formOrModal?.closest?.('[data-pdp-hint-modal]') ||
    formOrModal?.querySelector?.('[data-pdp-hint-modal]') ||
    formOrModal
  );
}

function pdpSendHintApplyVariant(modal) {
  const source = document.getElementById(modal?.dataset?.pdpHintVariantsId || '');
  if (!source) return;
  let variants;
  try {
    variants = JSON.parse(source.textContent);
  } catch (error) {
    return;
  }
  const variantId = source.dataset.currentVariantId;
  const entry = variantId && variants[variantId];
  if (!entry) return;

  const form = modal.querySelector('.pdp-send-hint-modal__form');
  if (form) {
    form.dataset.variantId = variantId;
    form.dataset.variantPrice = entry.price;
    form.dataset.productUrl = entry.url;
    if (entry.title) form.dataset.productTitle = entry.title;
    if (entry.src) form.dataset.productImageUrl = entry.src;
  }
  if (entry.src) {
    modal.querySelectorAll('.pdp-send-hint-modal__image--product').forEach((img) => {
      img.removeAttribute('srcset');
      img.src = entry.src;
    });
  }
}

function pdpSendHintModalsFor(variantsId) {
  const selector = `[data-pdp-hint-variants-id="${variantsId}"]`;
  const modals = [...document.querySelectorAll(selector)];
  const walkTemplates = (root) => {
    root.querySelectorAll('template').forEach((template) => {
      modals.push(...template.content.querySelectorAll(selector));
      walkTemplates(template.content);
    });
  };
  walkTemplates(document);
  return modals;
}

document.addEventListener('theme:variant:change', (event) => {
  const variant = event.detail?.variant;
  const productRoot = event.detail?.productRootEl;
  if (!variant?.id || !productRoot) return;
  productRoot.querySelectorAll('[data-pdp-hint-variants]').forEach((source) => {
    source.dataset.currentVariantId = String(variant.id);
    try {
      const src = JSON.parse(source.textContent)[variant.id]?.src;
      if (src) new Image().src = src;
    } catch (error) {
      // ignore malformed variant data
    }
    pdpSendHintModalsFor(source.id).forEach(pdpSendHintApplyVariant);
  });
});

window.theme.pdpSendHintResetModal = function (rootEl) {
  const modal = pdpSendHintModalRoot(rootEl);
  if (!modal) return;
  pdpSendHintApplyVariant(modal);
  const form = modal.querySelector('.pdp-send-hint-modal__form');
  const formPanel = modal.querySelector('[data-pdp-hint-form-panel]');
  const successPanel = modal.querySelector('[data-pdp-hint-success-panel]');
  const errorEl = modal.querySelector('[data-pdp-hint-error]');
  const submitBtn = modal.querySelector('[data-pdp-hint-submit]');

  if (form) form.reset();
  if (errorEl) {
    errorEl.textContent = '';
    errorEl.hidden = true;
  }
  if (formPanel) {
    formPanel.hidden = false;
    formPanel.removeAttribute('hidden');
  }
  if (successPanel) {
    successPanel.hidden = true;
    successPanel.setAttribute('hidden', '');
  }
  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-busy');
  }
  modal.querySelectorAll('.pdp-send-hint-modal__input').forEach((input) => {
    input.disabled = false;
  });
};

function pdpSendHintStateFromForm(form) {
  const ds = form?.dataset || {};
  return {
    senderName: '',
    recipientEmail: '',
    step: 'form',
    errorMessage: '',
    isSubmitting: false,
    metricName: ds.pdpHintMetric || 'Drop a Hint',
    listId: ds.klaviyoListId || '',
    product: {
      id: Number(ds.productId) || null,
      title: ds.productTitle || '',
      handle: ds.productHandle || '',
      pathname: ds.productPathname || '',
      url: ds.productUrl || '',
      imageUrl: ds.productImageUrl || '',
      variantId: Number(ds.variantId) || null,
      price: Number(ds.variantPrice) || 0,
    },
  };
}

function pdpSendHintSyncUi(form, state) {
  if (!form) return;
  const modal = pdpSendHintModalRoot(form);
  const errorEl = modal?.querySelector('[data-pdp-hint-error]');
  if (errorEl) {
    errorEl.textContent = state.errorMessage || '';
    errorEl.hidden = !state.errorMessage;
  }

  const formPanel = modal?.querySelector('[data-pdp-hint-form-panel]');
  const successPanel = modal?.querySelector('[data-pdp-hint-success-panel]');
  const showSuccess = state.step === 'success';

  if (formPanel) {
    formPanel.hidden = showSuccess;
    formPanel.toggleAttribute('hidden', showSuccess);
  }
  if (successPanel) {
    successPanel.hidden = !showSuccess;
    successPanel.toggleAttribute('hidden', !showSuccess);
  }

  const submitBtn = form.querySelector('[data-pdp-hint-submit]');
  if (submitBtn) {
    submitBtn.disabled = Boolean(state.isSubmitting);
    submitBtn.setAttribute('aria-busy', state.isSubmitting ? 'true' : 'false');
  }

  form.querySelectorAll('.pdp-send-hint-modal__input').forEach((input) => {
    input.disabled = Boolean(state.isSubmitting);
  });
}

window.theme.handlePdpSendHintSubmit = function (event) {
  event.preventDefault();
  const form = event.target;
  if (!form || !form.classList?.contains('pdp-send-hint-modal__form')) return;
  const state = pdpSendHintStateFromForm(form);
  return window.theme.submitPdpSendHint(state, form);
};

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('pdp-send-hint-modal__form')) return;
    event.preventDefault();
    event.stopPropagation();
    window.theme.handlePdpSendHintSubmit(event);
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const closeBtn = event.target.closest('[data-pdp-hint-close]');
    if (!closeBtn) return;
    event.preventDefault();
    window.Alpine?.store?.('modals')?.close?.('popup');
  },
  true
);

window.theme.submitPdpSendHint = async function (state, formEl) {
  const strings = window.theme.strings?.pdpSendHint || {};
  state.errorMessage = '';

  let senderName = String(state.senderName || '').trim();
  let recipientEmail = String(state.recipientEmail || '').trim();

  if (formEl) {
    const senderInput = formEl.querySelector('[data-pdp-hint-sender]');
    const emailInput = formEl.querySelector('[data-pdp-hint-recipient]');
    if (senderInput) {
      senderName = String(senderInput.value || '').trim();
      state.senderName = senderName;
    }
    if (emailInput) {
      recipientEmail = String(emailInput.value || '').trim();
      state.recipientEmail = recipientEmail;
    }
  }

  if (!senderName) {
    state.errorMessage = strings.senderRequired || 'Please enter your name.';
    pdpSendHintSyncUi(formEl, state);
    return;
  }
  if (!pdpSendHintIsValidEmail(recipientEmail)) {
    state.errorMessage = strings.emailInvalid || 'Please enter a valid email address.';
    pdpSendHintSyncUi(formEl, state);
    return;
  }

  const consentInput = formEl?.querySelector('[data-pdp-hint-consent]');
  if (consentInput && !consentInput.checked) {
    state.errorMessage = strings.consentRequired || 'Please check the box to continue.';
    pdpSendHintSyncUi(formEl, state);
    return;
  }

  state.isSubmitting = true;
  pdpSendHintSyncUi(formEl, state);
  const payload = { recipientEmail, senderName, product: state.product };
  if (state.listId) {
    try {
      await pdpSendHintSubscribeToList({ ...payload, listId: state.listId });
    } catch (error) {
      console.error('[PDP send hint] list subscribe failed', error);
    }
  }
  try {
    await pdpSendHintTrackKlaviyoEvent({ ...payload, metricName: state.metricName });
  } catch (error) {
    console.error('[PDP send hint]', error);
    state.isSubmitting = false;
    state.errorMessage = strings.submitError || 'Something went wrong. Please try again.';
    pdpSendHintSyncUi(formEl, state);
    return;
  }
  state.isSubmitting = false;
  state.step = 'success';
  state.errorMessage = '';
  pdpSendHintSyncUi(formEl, state);
};

async function loadCartDrawerComplementary(root, url) {
  if (!root || !url) return;
  root.innerHTML = await fetchCartComplementaryHTML(url);
  await hydrateCartComplementaryIslands(root);
}

function syncCartDrawerComplementaryRoot(cartItemsSectionHtml) {
  const edge = document.querySelector('.cart-drawer-complementary-edge');
  if (!edge || !cartItemsSectionHtml) return null;

  const doc = new DOMParser().parseFromString(cartItemsSectionHtml, 'text/html');
  const freshRoot = doc.querySelector('[data-cart-complementary-root]');
  const url = freshRoot?.dataset.complementaryUrl;

  if (!url) {
    const existingRoot = edge.querySelector('[data-cart-complementary-root]');
    if (existingRoot) existingRoot.remove();
    return null;
  }

  let root = edge.querySelector('[data-cart-complementary-root]');
  if (!root) {
    edge.insertAdjacentHTML('beforeend', freshRoot.outerHTML);
    root = edge.querySelector('[data-cart-complementary-root]');
  } else {
    root.dataset.complementaryUrl = url;
  }

  return { root, url };
}

function refreshCartDrawerComplementary(e) {
  const cartItemsHtml = e?.detail?.response?.sections?.['cart-items'];
  if (!cartItemsHtml) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(async () => {
      const synced = syncCartDrawerComplementaryRoot(cartItemsHtml);
      if (!synced) return;
      await loadCartDrawerComplementary(synced.root, synced.url);
    });
  });
}

function initCartDrawerComplementary() {
  const root = document.querySelector(
    '[data-cart-complementary-root][data-complementary-url]'
  );
  if (!root || root.innerHTML.trim() !== '') return;
  loadCartDrawerComplementary(root, root.dataset.complementaryUrl);
}

document.addEventListener('shapes:cart:update', refreshCartDrawerComplementary);
document.addEventListener('shapes:cart:cartqtychange', refreshCartDrawerComplementary);
document.addEventListener('shapes:cart:afteradditem', refreshCartDrawerComplementary);
document.addEventListener('theme:cart-drawer:opening', initCartDrawerComplementary);
document.addEventListener('switch:alpine:started', () => {
  requestIdleCallback(initCartDrawerComplementary);
});

const getCartDrawerEl = () => {
  return document.querySelector('[data-cart-drawer]');
};

function syncGiftNotePanelOpenFromIntent() {
  if (window.theme.syncDrawerGiftNotePanelFromIntent) {
    window.theme.syncDrawerGiftNotePanelFromIntent();
    return;
  }
  const compose = document.querySelector('[data-cart-drawer-gift-compose]');
  if (!compose) return;
  const cb = compose.querySelector('[data-cart-drawer-gift-checkbox]');
  const intentOn = isGiftCheckboxIntentPersisted();
  const panelShouldOpen = isGiftNotePanelOpenPersisted();
  if (!panelShouldOpen) return;
  if (!intentOn && !(cb && cb.checked)) return;
  if (cb && intentOn && !cb.checked) cb.checked = true;
  if (window.theme.setDrawerGiftNotePanelOpen) {
    window.theme.setDrawerGiftNotePanelOpen(true);
  }
}

window.theme.syncGiftNotePanelOpenFromIntent = syncGiftNotePanelOpenFromIntent;

/** Footer morph runs synchronously in `shapes:cart:*`; fix gift UI in a microtask so intent wins over stale markup. */
function scheduleCartDrawerGiftUISync() {
  queueMicrotask(() => {
    seedGiftIntentFromHiddenNoteField();
    applyGiftCheckboxIntentFromStorage();
    syncDrawerGiftCheckboxFromCart();
    syncGiftNotePanelOpenFromIntent();
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('shapes:cart:afteradditem', scheduleCartDrawerGiftUISync);
  window.addEventListener('shapes:cart:cartqtychange', scheduleCartDrawerGiftUISync);
  window.addEventListener('shapes:cart:update', scheduleCartDrawerGiftUISync);
}

document.addEventListener('cart-will-open', async () => {
  const cartDrawerEl = getCartDrawerEl();

  const detail = {
    cartDrawerEl,
  };

  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:opening', {
      detail,
    })
  );
});

document.addEventListener('cart-is-open', async () => {
  queueMicrotask(() => {
    seedGiftIntentFromHiddenNoteField();
    applyGiftCheckboxIntentFromStorage();
    syncDrawerGiftCheckboxFromCart();
    syncGiftNotePanelOpenFromIntent();
    if (window.theme.hydrateDrawerGiftUpsellDisplay) {
      window.theme.hydrateDrawerGiftUpsellDisplay();
    }
  });

  const cartDrawerEl = getCartDrawerEl();

  const detail = {
    cartDrawerEl,
  };

  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:open', {
      detail,
    })
  );
});

document.addEventListener('cart-will-close', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closing', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('cart-is-closed', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closed', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('theme:open:cart-drawer', () => {
  openCartDrawerFromTheme();
});

document.addEventListener('shapes:cart:afteradditem', () => {
  openCartDrawerFromTheme();
});

document.addEventListener('theme:close:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'modal' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').close('cart');
  }
});

const cartChangeEventHandler = (e) => {
  const { cartItemCount, itemsRootEl, lineItemEl, variantId, key } = e.detail;

  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      detail: {
        type: e.type,
        cartItemCount,
        itemsRootEl,
        lineItemEl,
        variantId,
        key,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener('theme:product:add', cartChangeEventHandler);
document.addEventListener('theme:line-item:change', cartChangeEventHandler);

const cartErrorEventHandler = (e) => {
  document.dispatchEvent(
    new CustomEvent('theme:cart:error', {
      detail: {
        type: e.type,
        message: e.detail.message,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener(
  'theme:product:error:add-to-cart',
  cartErrorEventHandler
);

document.addEventListener('theme:line-item:error', cartErrorEventHandler);
document.addEventListener('theme:cart:error:other', cartErrorEventHandler);

/**
 * Set `window.themeEventsDebugMode` to `true` to log events
 * outside the theme editor
 */

if (Shopify.designMode || window.themeEventsDebugMode) {
  const themeEvents = [
    'theme:variant:change',
    'theme:product:add',
    'theme:line-item:change',
    'theme:cart:change',
    'theme:cart:update',
    'theme:cart-drawer:opening',
    'theme:cart-drawer:open',
    'theme:cart-drawer:closing',
    'theme:cart-drawer:closed',
    'theme:product:error:add-to-cart',
    'theme:line-item:error',
    'theme:cart:error:other',
    'theme:cart:error',
  ];

  const aggregateEvents = ['theme:cart:change', 'theme:cart:error'];

  for (const eventName of themeEvents) {
    let color;

    if (eventName.includes('error')) {
      color = '#ef4444';
    } else {
      color = '#22c55e';
    }

    const message = [
      `%c\u25CF %c${eventName}`,
      `color: ${color};`,
      'font-weight: bold;',
    ];

    if (aggregateEvents.includes(eventName)) {
      document.addEventListener(eventName, (e) => {
        console.groupCollapsed(...message);
        console.log(
          `Underlying event: %c${e.detail.type}`,
          'font-weight: bold;'
        );
        console.log('Event detail:', e.detail);
        console.groupEnd();
      });
    } else {
      document.addEventListener(eventName, (e) => {
        console.log(...message, e.detail);
      });
    }
  }
}
